import { axiosInstance } from "@/lib/axios-instance";
import type {
  DealActionType,
  IChat,
  IDataSufficiencyScore,
  IDataSufficiencyScoreV2,
  IDealInfo,
  IDealMemo,
  IDealNote,
  IFundraising,
  IMarketIntelligence,
  IMetricItem,
  IQuestionnaire,
  IStartupScore,
  ISummary,
  ITearSheet,
  ModelDate,
  ReportStatusType,
} from "@/types/data-models";

export interface LinkedInEmployee {
  img: string;
  link: string;
  subtitle: string;
  title: string;
}

export interface LinkedInSimilarCompany {
  Links: string;
  subtitle: string;
  title: string;
  location?: string;
}

export interface LinkedInUpdate {
  likes_count: number;
  text: string;
  time: string;
  title: string;
  images?: string[];
  post_url: string;
  post_id: string;
  date: string;
  tagged_companies?: any;
  tagged_people?: any;
  repost?: {
    tagged_companies?: any;
    tagged_users?: any;
    images?: any;
    videos?: any;
    external_links?: any;
    repost_hangtags?: any;
  };
  text_html?: string;
  comments_count?: number;
}

export interface LinkedInFunding {
  last_round_date: string;
  last_round_type: string;
  rounds: number;
}

export interface LinkedInCompanyData {
  id: string;
  name: string;
  country_code: string;
  locations: string[];
  followers: number;
  employees_in_linkedin: number;
  about: string;
  specialties: string;
  company_size: string;
  organization_type: string;
  industries: string;
  website: string;
  crunchbase_url?: string;
  founded?: number;
  company_id: string;
  employees: LinkedInEmployee[];
  headquarters: string;
  image: string;
  logo: string;
  similar: LinkedInSimilarCompany[];
  url: string;
  updates: LinkedInUpdate[];
  slogan: string;
  funding?: LinkedInFunding;
  formatted_locations: string[];
  get_directions_url?: Array<{
    directions_url: string;
  }>;
  description: string;
  additional_information?: string;
  country_codes_array: string[];
  alumni?: any;
  alumni_information?: any;
  website_simplified: string;
  unformatted_about: string;
  timestamp: string;
  input: {
    url: string;
  };
}

export interface GetReportStatusResponse {
  status: string;
  message: string;
  opportunityApplication: {
    applicant_company: string;
    pertains_to_opportunity: string;
    changed_by?: string;
    custom_text_a1?: string;
    custom_text_a2?: string;
    application_status?: string;
    action_type?: DealActionType;
    created_on: Date;
    deal_info?: IDealInfo[];
    fundraising?: IFundraising;
    website_summary?: string;
    deal_notes?: IDealNote[];
    reports_in_progress?: {
      deal_memo?: {
        type: boolean;
        updated_at: Date;
      };
    };
    questionnaire?: IQuestionnaire[];
    source?: string;
    report_status?: {
      ingest?: ReportStatusType;
      summary?: ReportStatusType;
      tearsheet?: ReportStatusType;
      metrics?: ReportStatusType;
      market_intel?: ReportStatusType;
      datasufficiencyscore?: ReportStatusType;
      datasufficiencyscore_v2?: ReportStatusType;
      startupscore?: ReportStatusType;
      deal_info?: ReportStatusType;
      website_summary?: ReportStatusType;
      website_summary_v2?: ReportStatusType;
      website_summary_ingest?: ReportStatusType;
      market_research?: ReportStatusType;
      linkedin_company_scrape?: ReportStatusType;
      linkedin_company_ingest?: ReportStatusType;
      gap_analysis?: ReportStatusType;
      deal_fit?: ReportStatusType;
      insights?: ReportStatusType;
    };
  };
}

export const getReportStatus = async (
  companyId: string,
  opportunityId: string
): Promise<GetReportStatusResponse> => {
  const response = await axiosInstance.get(
    `/get_report_status?company_id=${companyId}&opportunity_id=${opportunityId}`
  );
  return response.data;
};

interface GetReportsResponse {
  status: string;
  data: {
    startupscores?: Array<{
      _id: string;
      startupscore: IStartupScore;
      created_on: ModelDate;
      credits_used: number;
    }>;
    datasufficiencyscores?: Array<{
      _id: string;
      datasufficiencyscore: IDataSufficiencyScore;
      created_on: ModelDate;
      credits_used: number;
    }>;
    datasufficiencyscores_v2?: Array<{
      _id: string;
      datasufficiencyscore_v2: IDataSufficiencyScoreV2;
      created_on: ModelDate;
      credits_used: number;
    }>;
    summaries?: Array<{
      _id: string;
      summary: ISummary;
      created_on: ModelDate;
      credits_used: number;
    }>;
    tearsheets?: Array<{
      _id: string;
      tearsheet: ITearSheet;
      created_on: ModelDate;
      credits_used: number;
    }>;
    chats?: Array<{
      _id: string;
      chat: IChat[];
      created_on: ModelDate;
      credits_used: number;
    }>;
    metrics?: Array<{
      _id: string;
      metrics: IMetricItem[];
      created_on: ModelDate;
      credits_used?: number;
    }>;
    market_intel?: Array<{
      _id: string;
      market_intel: IMarketIntelligence;
      created_on: ModelDate;
      credits_used: number;
    }>;
    dealMemo?: Array<{
      _id: string;
      dealMemo: IDealMemo;
      created_on: ModelDate;
      credits_used: number;
    }>;
    dealInfo?: IDealInfo[];
    websiteInfo?: {
      website_summary: string;
      created_on?: ModelDate;
    };
    website_summary_v2?: Array<{
      _id: string;
      webpage_list: Array<{
        webpage_link: string;
        file_url: string;
        file_size: string;
        timestamp: ModelDate;
        summary: string;
      }>;
      website_summary: string;
      main_webpage_link: string;
      created_on: ModelDate;
      credits_used?: number;
    }>;
    market_research?: Array<{
      _id: string;
      market_research: Array<{
        model: string;
        [key: string]: any;
      }>;
      market_research_final: any;
      created_on: ModelDate;
      credits_used?: number;
    }>;
    linkedInInfo?: Array<{
      _id: string;
      data: LinkedInCompanyData | null;
      timestamp?: Date | string;
      created_on?: ModelDate;
    }>;
  };
}

export const getReports = async (
  companyId: string,
  opportunityId: string,
  reportType?: string
): Promise<GetReportsResponse> => {
  const url = `/get_reports?company_id=${companyId}&opportunity_id=${opportunityId}${
    reportType ? `&report_type=${reportType}` : ""
  }`;
  const response = await axiosInstance.get(url);
  return response.data;
};
